import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react'
import withResolution from 'hoc/withResolution'
import { addDays, addMinutes, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek } from 'date-fns'
import { useScheduleApi } from 'hooks/schedule/useScheduleApi'
import { orderBy } from 'lodash'
import ProviderScheduleCalendar, {
  ProviderScheduleCalendarRef,
  SCHEDULE_WEEK_STARTS_ON,
  ProviderScheduleSlotsFilter,
} from '@ui-components-3/provider-schedule-calendar'
import { observer } from 'mobx-react-lite'
import { Navigate, useNavigate } from 'react-router'
import { useLiveCurrentDate } from 'hooks/useLiveCurrentDate'
import User from 'singletons/User'
import { DASHBOARD, DASHBOARD_SCHEDULE_V1, HELP_CENTER } from 'constants/routing'

type ProviderScheduleCalendarProps = ComponentProps<typeof ProviderScheduleCalendar>

const Schedule = ({ isMobile }: { isMobile: boolean }) => {
  const [date, setDate] = useState(() => new Date())
  const [calendarMode, setCalendarMode] = useState<ProviderScheduleCalendarProps['mode']>('week')
  const providerScheduleCalendarRef = useRef<ProviderScheduleCalendarRef>(null)

  const nowDate = useLiveCurrentDate()

  const navigate = useNavigate()

  const isWeekMode = calendarMode === 'week'

  const getCalendarModeStartDate = useCallback(
    (dateToCheck: Date) =>
      isWeekMode
        ? startOfWeek(new Date(dateToCheck), {
            weekStartsOn: SCHEDULE_WEEK_STARTS_ON,
          })
        : startOfMonth(new Date(dateToCheck)),
    [isWeekMode],
  )

  const startDate = useMemo(() => getCalendarModeStartDate(date), [date, getCalendarModeStartDate])

  const endDate = useMemo(
    () =>
      isWeekMode ? endOfWeek(new Date(date), { weekStartsOn: SCHEDULE_WEEK_STARTS_ON }) : endOfMonth(new Date(date)),
    [date, isWeekMode],
  )

  const calendarModeStartDate = useMemo(() => getCalendarModeStartDate(nowDate), [getCalendarModeStartDate, nowDate])

  const isPastDate = useMemo(
    () => startDate.valueOf() < calendarModeStartDate.valueOf(),
    [calendarModeStartDate, startDate],
  )

  const isFutureDate = useMemo(
    () => startDate.valueOf() > calendarModeStartDate.valueOf(),
    [calendarModeStartDate, startDate],
  )

  // we add 2 days to endDate and -2 to startDate to make sure
  // that we don't miss any slots because of browser-server timezone difference
  const slotsFilter = useMemo<ProviderScheduleSlotsFilter>(
    () => ({
      startDate: format(addDays(startDate, -2), 'yyyy-MM-dd'),
      endDate: format(addDays(endDate, 2), 'yyyy-MM-dd'),
    }),
    [endDate, startDate],
  )

  const {
    submitSchedule,
    isSubmittingSchedule,
    schedules,
    isFetchingSchedules,
    slots,
    isSlotsFetching,
    deleteSlot,
    deleteSchedule,
    isDeleting,
  } = useScheduleApi({
    onSuccessCreateOrUpdate: () => providerScheduleCalendarRef?.current?.resetDrawer(),
    onErrorCreateOrUpdate: () => providerScheduleCalendarRef?.current?.showCreateError(),
    slotsFilter,
    shouldFetchSlots: !isPastDate,
    shouldFetchVisits: !isFutureDate,
  })

  const { isV3Schedule } = User.provider?.scheduleVersionConfig || {}

  // filter out slots from prev/next week/month that could be in the list because of timezone difference
  const filteredSlots = useMemo(
    () =>
      orderBy(
        slots?.filter((i) => {
          const itemStartTimestamp = new Date(i.startTime).valueOf()
          const itemEndTimestamp = addMinutes(new Date(itemStartTimestamp), i.duration).valueOf()

          const isInCalendarDateRange =
            itemStartTimestamp >= new Date(startDate).valueOf() && itemStartTimestamp <= new Date(endDate).valueOf()

          // filter out slots that are outside the current calendar view
          if (!isInCalendarDateRange) {
            return false
          }

          // filter out booked slots if provider has not migrated to v3 schedule yet
          if (!!i.booked && !isV3Schedule) {
            return false
          }

          const nowTimestamp = new Date(nowDate).valueOf()

          // filter out non-past fake pending visit slots
          if (!!i.booked && !!i.isFakeSlot) {
            return itemEndTimestamp < nowTimestamp
          }

          // filter out past not booked slots
          return !i.isFakeSlot && itemEndTimestamp > nowTimestamp
        }) || [],
        [(i) => new Date(i.startTime).valueOf()],
        ['asc'],
      ),
    [endDate, isV3Schedule, nowDate, slots, startDate],
  )

  const onOpenVisit = useCallback(
    (pendingVisitId: string) => {
      navigate(`/visits/${pendingVisitId}`)
    },
    [navigate],
  )

  const { showV1Schedule, showV3Schedule } = User?.provider?.scheduleVersionConfig || {}

  if (!showV3Schedule) {
    return <Navigate to={showV1Schedule ? DASHBOARD_SCHEDULE_V1 : DASHBOARD} replace />
  }

  return (
    <ProviderScheduleCalendar
      ref={providerScheduleCalendarRef}
      isMobile={isMobile}
      colorVariant="primary"
      mode={calendarMode}
      onChangeMode={setCalendarMode}
      date={date}
      onChangeDate={setDate}
      submitSchedule={submitSchedule}
      isSubmittingSchedule={isSubmittingSchedule}
      schedules={schedules}
      isFetchingSchedules={isFetchingSchedules}
      isSlotsFetching={isSlotsFetching}
      slots={filteredSlots}
      onOpenVisit={onOpenVisit}
      deleteSlot={deleteSlot}
      deleteSchedule={deleteSchedule}
      isDeleting={isDeleting}
      minTimeSegmentDuration={15}
      contactSupportLink={HELP_CENTER}
    />
  )
}

export default withResolution(1200, false, false)(observer(Schedule))
